// Kula Create composition — the spec types (Phase 1).
//
// A "composed experience" is a bespoke interaction the AI/designer assembles from
// a small set of capturing PRIMITIVE BLOCKS instead of picking one of the fixed
// interaction types. It renders as a native Kula component and reports readable
// learner data — the defining property of every Create output.
//
// Design constraints baked into these types (from the handover §3):
//   - Blocks run top-to-bottom, always, in order (Phase 1 has no logic layer).
//   - Every block cleanly separates `structure` (type/ref/capture class/display
//     settings — the SHAPE) from `content` (all authored text/media — the
//     CONTENT). Stake 1: a "shape extract" is a mechanical strip of `content`,
//     yielding a valid, content-free template. Never interleave the two.
//   - `ref` (data-flow only in Phase 1) lets a block re-ask / reveal-against an
//     upstream block — the mechanism behind before/after deltas, predict/reveal
//     gaps, confidence shifts. NOT branching or conditional visibility (Phase 2).
//   - `captureClass` is load-bearing: 'actionable' (gradeable), 'articulation'
//     (retained prose, shown back, never auto-scored), 'none' (no capture).
//
// Pure module — no React, no network. Safe to import anywhere.

// ---------------------------------------------------------------------------
// Primitive vocabulary (Wave 1 — the set is fixed; the AI composes, it does not
// invent primitives).
// ---------------------------------------------------------------------------

export type PrimitiveType =
  // --- Wave 1 ---
  | 'stimulus' // reads a framed situation (no input)
  | 'choice' // picks one option, gets feedback
  | 'rate-slider' // places a weighting on a continuum
  | 'sort-zones' // drags items into labelled zones
  | 'select-evidence' // picks from a pool with distractors
  | 'reveal' // uncovers an expert view after a commit (consumes a ref)
  | 'commit-box' // states a specific, constrained commitment (articulation)
  // --- Wave 2 ---
  | 'confidence' // self-reports confidence on a scale (never graded; pairs for a shift)
  | 'flag-in-text' // clicks the wrong/weak spans in a passage (spot-the-error mechanic)
  | 'perspective-lens'; // writes a short take from each of several named lenses (articulation)

export const PRIMITIVE_TYPES: readonly PrimitiveType[] = [
  'stimulus',
  'choice',
  'rate-slider',
  'sort-zones',
  'select-evidence',
  'reveal',
  'commit-box',
  'confidence',
  'flag-in-text',
  'perspective-lens',
] as const;

/**
 * What the platform can do with a block's captured value.
 * - `actionable`  — gradeable (selection, placement, position, delta).
 * - `articulation`— readable prose; retained + shown back, never auto-scored.
 * - `none`        — the block captures nothing (stimulus, reveal).
 */
export type CaptureClass = 'actionable' | 'articulation' | 'none';

// ---------------------------------------------------------------------------
// Block schema. Every block shares the structural envelope; the `structure` and
// `content` payloads are primitive-specific and rigorously split (Stake 1).
// ---------------------------------------------------------------------------

interface BlockEnvelope<T extends PrimitiveType> {
  id: string;
  type: T;
  /** OPTIONAL upstream block this one re-asks / reveals against (data-flow only). */
  ref?: string;
  captureClass: CaptureClass;
}

// --- stimulus ---------------------------------------------------------------
export interface StimulusBlock extends BlockEnvelope<'stimulus'> {
  captureClass: 'none';
  structure: Record<string, never>;
  content: {
    heading?: string;
    body: string;
  };
}

// --- choice -----------------------------------------------------------------
export interface ChoiceOption {
  id: string;
  text: string;
  /** Exactly one option SHOULD be correct when this choice is scored. */
  correct?: boolean;
  /** Shown after the learner picks this option. */
  feedback?: string;
}
export interface ChoiceBlock extends BlockEnvelope<'choice'> {
  captureClass: 'actionable';
  structure: Record<string, never>;
  content: {
    prompt: string;
    options: ChoiceOption[];
  };
}

// --- rate-slider ------------------------------------------------------------
export interface RateSliderBlock extends BlockEnvelope<'rate-slider'> {
  captureClass: 'actionable';
  structure: {
    min: number;
    max: number;
    step?: number;
    /**
     * OPTIONAL scoring target. When present the block is gradeable: a position
     * within `tolerance` of `value` scores 1, degrading linearly to 0. Absent =
     * the block captures a position/weighting that is NOT auto-scored (it can
     * still feed a `reveal` delta or the close via ref).
     */
    target?: { value: number; tolerance: number };
  };
  content: {
    prompt: string;
    minLabel?: string;
    maxLabel?: string;
  };
}

// --- sort-zones -------------------------------------------------------------
export interface SortZone {
  id: string;
  label: string;
}
export interface SortItem {
  id: string;
  text: string;
  /** The zone this item belongs in (scored against). */
  correctZone: string;
}
export interface SortZonesBlock extends BlockEnvelope<'sort-zones'> {
  captureClass: 'actionable';
  structure: Record<string, never>;
  content: {
    prompt: string;
    zones: SortZone[];
    items: SortItem[];
  };
}

// --- select-evidence --------------------------------------------------------
export interface EvidenceItem {
  id: string;
  text: string;
  /** True = part of the expert set (the pieces that genuinely support the claim). */
  strong?: boolean;
}
export interface SelectEvidenceBlock extends BlockEnvelope<'select-evidence'> {
  captureClass: 'actionable';
  structure: {
    /** Max picks the learner may make. Defaults to the strong-item count. */
    maxSelections?: number;
  };
  content: {
    prompt: string;
    /** The claim the evidence is assembled for. */
    claim?: string;
    items: EvidenceItem[];
  };
}

// --- reveal -----------------------------------------------------------------
export interface RevealBlock extends BlockEnvelope<'reveal'> {
  captureClass: 'none';
  /** REQUIRED: the upstream actionable block this reveal pairs with. */
  ref: string;
  structure: Record<string, never>;
  content: {
    /** The expert view / model answer surfaced after the learner commits. */
    heading?: string;
    body: string;
  };
}

// --- commit-box -------------------------------------------------------------
export interface CommitBoxBlock extends BlockEnvelope<'commit-box'> {
  captureClass: 'articulation';
  structure: {
    minWords?: number;
    maxWords?: number;
  };
  content: {
    prompt: string;
    placeholder?: string;
  };
}

// --- confidence (Wave 2) -----------------------------------------------------
// A self-report on a scale. Actionable (captured, can pair for a shift) but
// NEVER graded — a learner's confidence is not right or wrong.
export interface ConfidenceBlock extends BlockEnvelope<'confidence'> {
  captureClass: 'actionable';
  structure: {
    min: number;
    max: number;
    step?: number;
  };
  content: {
    prompt: string;
    minLabel?: string;
    maxLabel?: string;
  };
}

// --- flag-in-text (Wave 2) ---------------------------------------------------
// The learner clicks the wrong / weak spans in a passage (the spot-the-error
// mechanic, composed). Segments flagged `true` are the ones that should be
// caught; the rest are the honest text (over-flagging is penalised).
export interface TextSegment {
  id: string;
  text: string;
  /** True = this span is wrong/weak and should be flagged. */
  flagged?: boolean;
}
export interface FlagInTextBlock extends BlockEnvelope<'flag-in-text'> {
  captureClass: 'actionable';
  structure: Record<string, never>;
  content: {
    prompt: string;
    segments: TextSegment[];
  };
}

// --- perspective-lens (Wave 2) -----------------------------------------------
// The learner writes a short take from each of several named lenses
// (stakeholder, discipline, timeframe…). Articulation — retained + shown back,
// never graded.
export interface Lens {
  id: string;
  label: string;
  hint?: string;
}
export interface PerspectiveLensBlock extends BlockEnvelope<'perspective-lens'> {
  captureClass: 'articulation';
  structure: Record<string, never>;
  content: {
    prompt: string;
    lenses: Lens[];
  };
}

export type CompositionBlock =
  | StimulusBlock
  | ChoiceBlock
  | RateSliderBlock
  | SortZonesBlock
  | SelectEvidenceBlock
  | RevealBlock
  | CommitBoxBlock
  | ConfidenceBlock
  | FlagInTextBlock
  | PerspectiveLensBlock;

// ---------------------------------------------------------------------------
// Experience-level scoring (§3.4). Explicit in the spec, never implicit in the
// runtime — with arbitrary compositions "the grade" needs declared semantics.
// ---------------------------------------------------------------------------

export interface CompositionScoring {
  /**
   * - `mean`     — unweighted mean of per-block normalised scores (default).
   * - `weighted` — weighted mean; each counted block carries a `weight`.
   * - `single`   — a single block IS the grade.
   * - `none`     — the experience is NOT graded. It still captures the learner's
   *   positions/articulations (a values clarification, opinion, or reflection
   *   where there is no right answer), but produces no rollup score. `blocks`
   *   is empty; it records as a survey, not a quiz.
   */
  mode: 'mean' | 'weighted' | 'single' | 'none';
  /**
   * Which actionable blocks count toward the grade, and (for `weighted`) how
   * much. Articulation and none-capture blocks may never appear here. Empty for
   * mode `none`.
   */
  blocks: Array<{ id: string; weight?: number }>;
}

// ---------------------------------------------------------------------------
// The composed experience, as it lives on a slide's data.
// (The standalone §3.1 "Experience object" carries `kind`/`version`/`theme`;
// on a slide those come from the slide envelope + course theme, so the
// slide-data form carries only what is experience-specific.)
// ---------------------------------------------------------------------------

export interface ComposedExperienceSpec {
  intent: string;
  blocks: CompositionBlock[];
  scoring: CompositionScoring;
}

// ---------------------------------------------------------------------------
// Narrowing helpers (used by the runtime + validator).
// ---------------------------------------------------------------------------

export const isActionable = (b: CompositionBlock): boolean => b.captureClass === 'actionable';
export const isArticulation = (b: CompositionBlock): boolean => b.captureClass === 'articulation';

/** Actionable blocks that can produce a normalised [0,1] score (are gradeable). */
export function isScoreable(b: CompositionBlock): boolean {
  switch (b.type) {
    case 'choice':
      return b.content.options.some((o) => o.correct === true);
    case 'sort-zones':
      return b.content.items.length > 0;
    case 'select-evidence':
      return b.content.items.some((i) => i.strong === true);
    case 'rate-slider':
      return b.structure.target !== undefined;
    case 'flag-in-text':
      return b.content.segments.some((s) => s.flagged === true);
    // confidence is actionable but a self-report — never graded.
    default:
      return false;
  }
}
